import React, { useEffect, useMemo, useState } from "react";
import TableGeneric from "../../../../shared/components/generic/table/table-generic";
import Pagination from "../../../../shared/components/generic/pagination/pagination";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import FormModal from "../../../../shared/components/generic/form/form-modal/form-modal";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";
import { variluxGoalsColumnsConfig } from "../../config/objetivos-varilux/varilux-goals-columns-config";
import { variluxGoalsFormConfig } from "../../config/objetivos-varilux/varilux-goals-form-config";
import AdminSection from "../../components/admin-section/admin-section";
import { useAdminFormModal } from "../../hooks/useAdminFormModal";
import { confirmSaveChanges, showNoRecordsFoundAlert } from "../../utils/admin-utils";
import { useSegmentations } from "../../hooks/segmentacao/useSegmentations";
import { useVariluxGoals } from "../../hooks/objetivos-varilux/useVariluxGoals";
import { useVariluxGoalsById } from "../../hooks/objetivos-varilux/useVariluxGoalsById";
import { useVariluxGoalsNew } from "../../hooks/objetivos-varilux/useVariluxGoalsNew";
import { useVariluxGoalsUpdate } from "../../hooks/objetivos-varilux/useVariluxGoalsUpdate";
import {
	hasValidationErrors,
	removeClearedValidationErrors
} from "../../../utils/validation-utils";
import { useVariluxGoalsDelete } from "../../hooks/objetivos-varilux/useVariluxGoalsDelete";
import { showConfirmAlert } from "../../../../shared/components/generic/alert/confirm-alert/confirm-alert";

const SEGMENTATIONS_SELECT_PAGE_SIZE = 9999;

const isConfirmAccepted = result => {
	return result?.isConfirmed === true || result?.value === true;
};

const ObjetivosVarilux = () => {
	const variluxGoalsModal = useAdminFormModal();

	const [segmentationsForSelect, setSegmentationsForSelect] = useState([]);
	const [variluxGoalsActionLoadingMessage, setVariluxGoalsActionLoadingMessage] = useState("");
	const [variluxGoalsValidationErrors, setVariluxGoalsValidationErrors] = useState({});
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});
	const [variluxGoalsPagination, setVariluxGoalsPagination] = useState({
		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 10
	});
	const [pendingNoRecordsAlert, setPendingNoRecordsAlert] = useState(false);

	const {
		variluxGoals,
		loading: variluxGoalsLoading,
		refetch: fetchVariluxGoals
	} = useVariluxGoals();

	const {
		loading: variluxGoalsByIdLoading,
		fetchVariluxGoalsById
	} = useVariluxGoalsById();

	const {
		loading: createVariluxGoalsLoading,
		validateVariluxGoals,
		createVariluxGoals
	} = useVariluxGoalsNew();

	const {
		loading: updateVariluxGoalsLoading,
		updateVariluxGoals
	} = useVariluxGoalsUpdate();

	const {
			loading: deleteVariluxGoalsLoading,
			deleteVariluxGoals
	} = useVariluxGoalsDelete();

	const {
		loading: segmentationsLoading,
		refetch: fetchSegmentations
	} = useSegmentations();

	const isLoadingVariluxGoals =
		variluxGoalsLoading ||
		variluxGoalsByIdLoading ||
		segmentationsLoading;

	const isSavingVariluxGoals =
		createVariluxGoalsLoading ||
		updateVariluxGoalsLoading;

	const isDeletingVariluxGoals = deleteVariluxGoalsLoading;

	const visibleVariluxGoalsValidationErrors = removeClearedValidationErrors({
		errors: variluxGoalsValidationErrors,
		clearedErrors: clearedValidationErrors
	});

	const loadVariluxGoals = async (
		pagination = variluxGoalsPagination,
		{ showEmptyAlert = true } = {}
	) => {
		const result = await fetchVariluxGoals({
			page: pagination.page,
			size: pagination.size
		});

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar as metas Varilux.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return result;
		}

		setVariluxGoalsPagination(result.pagination);

		if (result.empty && showEmptyAlert) {
			setPendingNoRecordsAlert(true);
		}

		return result;
	};

	const loadSegmentationsForSelect = async () => {
		const result = await fetchSegmentations({
			page: 0,
			size: SEGMENTATIONS_SELECT_PAGE_SIZE
		});

		if (!result.ok) {
			setSegmentationsForSelect([]);
			return;
		}

		setSegmentationsForSelect(result.segmentations || []);
	};

	useEffect(() => {
		loadVariluxGoals({
			page: 0,
			size: variluxGoalsPagination.size
		});

		loadSegmentationsForSelect();
	}, []);

	useEffect(() => {
		if (!pendingNoRecordsAlert) {
			return;
		}

		if (isLoadingVariluxGoals || variluxGoalsActionLoadingMessage) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setPendingNoRecordsAlert(false);
			showNoRecordsFoundAlert();
		}, 0);

		return () => clearTimeout(timeoutId);
	}, [
		pendingNoRecordsAlert,
		isLoadingVariluxGoals,
		variluxGoalsActionLoadingMessage
	]);

	const handleOpenCreate = () => {
		setVariluxGoalsValidationErrors({});
		setClearedValidationErrors({});
		variluxGoalsModal.openCreate();
	};

	const handleEdit = async row => {
		setVariluxGoalsValidationErrors({});
		setClearedValidationErrors({});

		const result = await fetchVariluxGoalsById(
			row.idSegmentation,
			row.id
		);

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os dados da meta Varilux.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		variluxGoalsModal.openEdit({
			...row,
			...result.variluxGoals,
			idSegmentation: result.variluxGoals.idSegmentation || row.idSegmentation
		});
	};

	const handleCloseVariluxGoalsModal = () => {
		setVariluxGoalsValidationErrors({});
		setClearedValidationErrors({});
		variluxGoalsModal.close();
	};

	const handleVariluxGoalsFieldChange = ({ name }) => {
		setClearedValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};

	const handleSubmitVariluxGoals = async values => {
		const selectedItem = variluxGoalsModal.selectedItem;
		const isEditing = variluxGoalsModal.isEditing;
		const segmentationId = values.segmentation || selectedItem?.idSegmentation;

		const valuesToValidate = {
			...values,
			segmentation: segmentationId
		};

		const validationErrors = validateVariluxGoals(valuesToValidate);

		if (hasValidationErrors(validationErrors)) {
			setClearedValidationErrors({});
			setVariluxGoalsValidationErrors(validationErrors);

			await showCommonAlert({
				title: "Atenção",
				html: Object.values(validationErrors).join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setVariluxGoalsValidationErrors({});
		setClearedValidationErrors({});
		variluxGoalsModal.close();

		const confirmResult = await confirmSaveChanges({
			description: "Deseja salvar as alterações?"
		});

		if (!isConfirmAccepted(confirmResult)) {
			return;
		}

		let saveResult;

		setVariluxGoalsActionLoadingMessage("Salvando meta Varilux...");

		try {
			saveResult = isEditing
				? await updateVariluxGoals(segmentationId, selectedItem.id, values)
				: await createVariluxGoals(segmentationId, values);

			if (saveResult.ok) {
				await loadVariluxGoals({
					page: isEditing ? variluxGoalsPagination.page : 0,
					size: variluxGoalsPagination.size
				});
			}
		} finally {
			setVariluxGoalsActionLoadingMessage("");
		}

		if (!saveResult.ok) {
			await showCommonAlert({
				title: "Erro",
				message: saveResult.error,
				fallbackMessage: "Não foi possível salvar a meta Varilux.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		await showCommonAlert({
			title: "Sucesso",
			message: isEditing
				? "Meta Varilux atualizada com sucesso."
				: "Meta Varilux incluída com sucesso.",
			icon: "success",
			customClass: {
				container: "common-alert__container",
				confirmButton: "alert-form-button"
			}
		});
	};

	const handleDelete = row => {
		showConfirmAlert({
			title: "Exclusão",
			description: `Deseja excluir a meta Varilux do tamanho do cliente "${row.segmentation}"?`,
			confirmLabel: "Sim, excluir",
			cancelLabel: "Cancelar"
		}).then(async result => {
			if (!isConfirmAccepted(result)) {
				return;
			}

			let deleteResult;
			let reloadResult;

			setVariluxGoalsActionLoadingMessage("Excluindo meta Varilux...");

			try {
				deleteResult = await deleteVariluxGoals(
					row.idSegmentation,
					row.id
				);

				if (deleteResult.ok) {
					reloadResult = await loadVariluxGoals(
						{
							page: variluxGoalsPagination.page,
							size: variluxGoalsPagination.size
						},
						{
							showEmptyAlert: false
						}
					);
				}
			} finally {
				setVariluxGoalsActionLoadingMessage("");
			}

			if (!deleteResult.ok) {
				await showCommonAlert({
					title: "Erro",
					message: deleteResult.error,
					fallbackMessage: "Não foi possível excluir a meta Varilux.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			await showCommonAlert({
				title: "Sucesso",
				message: "Meta Varilux excluída com sucesso.",
				icon: "success",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			if (reloadResult?.empty) {
				await showNoRecordsFoundAlert();
			}
		});
	};

	const handlePageChange = ({ selected }) => {
		loadVariluxGoals({
			...variluxGoalsPagination,
			page: selected
		});
	};

	const columns = useMemo(() => {
		return variluxGoalsColumnsConfig({
			onEdit: handleEdit,
			onDelete: handleDelete
		});
	}, []);

	const formFields = useMemo(() => {
		return variluxGoalsFormConfig({
			variluxGoals: variluxGoalsModal.selectedItem,
			segmentations: segmentationsForSelect,
			loadingSegmentations: segmentationsLoading,
			isEditing: variluxGoalsModal.isEditing
		});
	}, [
		variluxGoalsModal.selectedItem,
		variluxGoalsModal.isEditing,
		segmentationsForSelect,
		segmentationsLoading
	]);

	return (
		<>
			<PageLoading
				disabled={variluxGoalsModal.isOpen}
				actionMessage={variluxGoalsActionLoadingMessage}
				states={[
					{
						loading: isLoadingVariluxGoals,
						message: "Carregando metas Varilux..."
					},
					{
						loading: isDeletingVariluxGoals,
						message: "Excluindo meta Varilux..."
					},
					{
						loading: isSavingVariluxGoals,
						message: "Salvando meta Varilux..."
					}
				]}
			/>

			<AdminSection
				title="Meta de crescimento em vendas de pares de Varilux personalizado"
				actionLabel="Incluir meta Varilux"
				actionStartIcon="plusCircle"
				onAction={handleOpenCreate}
			>
				<TableGeneric
					columns={columns}
					data={variluxGoals}
					rowKey="id"
				/>

				{variluxGoalsPagination.totalPages > 1 && (
					<Pagination
						pageCount={variluxGoalsPagination.totalPages}
						currentPage={variluxGoalsPagination.page}
						onPageChange={handlePageChange}
					/>
				)}
			</AdminSection>

			<FormModal
				open={variluxGoalsModal.isOpen}
				title={variluxGoalsModal.isEditing ? "Editar meta Varilux" : "Incluir meta Varilux"}
				formId="varilux-goals-form"
				formFields={formFields}
				errors={visibleVariluxGoalsValidationErrors}
				onClose={handleCloseVariluxGoalsModal}
				onSubmit={handleSubmitVariluxGoals}
				onFieldChange={handleVariluxGoalsFieldChange}
				submitLabel="Salvar"
				cancelLabel="Cancelar"
				submitDisabled={
					isSavingVariluxGoals ||
					isLoadingVariluxGoals ||
					Boolean(variluxGoalsActionLoadingMessage)
				}
			/>
		</>
	);
};

export default ObjetivosVarilux;